/**
 * Agent run type definitions.
 *
 * Types are inferred from Zod schemas (`../schemas/agentRun.schemas`) for
 * runtime validation, mirroring `frontend/src/features/workspace/types/`.
 * The schemas themselves are derived from the real backend response
 * models in `backend/features/agent_runs/schemas.py` (RunResponse,
 * RunDetailResponse, RunEventResponse) — not from the task brief's memory
 * of them. Differences worth noting for later readers:
 *  - `project_id` is the Sanctum project UUID (FK to `projects.id`), never
 *    a GitLab-side identifier. `project_path`, `pipeline_url`, `job_url`,
 *    and `mr_url` are derived server-side (backend
 *    `services/run_links_service.py`, joining Project -> GitServer) --
 *    this frontend never builds a GitLab URL itself, since it has no
 *    GitLab base URL of its own. Each is nullable independently: a
 *    project/server that can't be resolved, a null `pipeline_id`/`job_id`,
 *    or an absent/malformed `result.mr_iid` all degrade to `null` rather
 *    than a malformed URL.
 *  - `budget_seconds`/`budget_tokens` are LIMITS. Consumption is `usage`,
 *    from the AI gateway's record of the run's own token.
 *  - `requested_by_user_id` is intentionally NOT part of `RunResponse` and
 *    so is not modeled here; the backend already scopes visibility server
 *    side (see router.py `_require_owned_run`).
 */

export type {
  AgentRun,
  AgentRunDetail,
  AgentRunEvent,
  IdentityMode,
  RunEventType,
  RunKind,
  RunStatus,
  RunUsage,
} from '../schemas/agentRun.schemas';

import type { RunStatus } from '../schemas/agentRun.schemas';

/** Mirrors backend `run_service.TERMINAL_STATUSES` — kept in sync by hand. */
const TERMINAL_RUN_STATUSES: ReadonlySet<RunStatus> = new Set([
  'succeeded',
  'failed',
  'denied',
  'stopped',
  'expired',
]);

export function isTerminalRunStatus(status: RunStatus): boolean {
  return TERMINAL_RUN_STATUSES.has(status);
}
